import { nombreEspecialidad, useEspecialidades } from '@/features/preguntas/queries';

import { agruparLicencias, useResumenBiblioteca } from './queries';

type Clave = 'q' | 'type' | 'specialtyId' | 'membershipId' | 'english' | 'unassigned';

/** Búsqueda y filtros del índice y de la matriz (valores en la URL vía `useFiltrosUrl`). */
export function FiltrosBiblioteca<K extends string>({
  filtros,
  cambiar,
  claves,
}: {
  filtros: Record<K, string>;
  cambiar: (c: Partial<Record<K, string>>) => void;
  claves: readonly K[];
}) {
  const resumen = useResumenBiblioteca();
  const esp = useEspecialidades();
  const tiene = (k: Clave) => (claves as readonly string[]).includes(k);
  const valor = (k: Clave) => (filtros as Record<string, string>)[k] ?? '';
  const poner = (k: Clave, v: string) => cambiar({ [k]: v } as Partial<Record<K, string>>);
  return (
    <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <input
        key={valor('q')}
        className="input-vidrio xl:col-span-2"
        placeholder="ID, nombre, código o etiqueta"
        defaultValue={valor('q')}
        onKeyDown={(e) => e.key === 'Enter' && poner('q', e.currentTarget.value.trim())}
        onBlur={(e) => poner('q', e.target.value.trim())}
        aria-label="Buscar"
      />
      {tiene('type') && (
        <select
          className="input-vidrio"
          value={valor('type')}
          onChange={(e) => poner('type', e.target.value)}
          aria-label="Tipo"
        >
          <option value="">Todos los tipos</option>
          {resumen.data?.types.map((t) => (
            <option key={t.type} value={t.type}>
              {t.label} ({t.count})
            </option>
          ))}
        </select>
      )}
      {tiene('specialtyId') && (
        <select
          className="input-vidrio"
          value={valor('specialtyId')}
          onChange={(e) => poner('specialtyId', e.target.value)}
          aria-label="Especialidad"
        >
          <option value="">Todas las especialidades</option>
          {esp.data?.map((e) => (
            <option key={e.id} value={e.id}>
              {nombreEspecialidad(e)}
            </option>
          ))}
        </select>
      )}
      {tiene('membershipId') && (
        <select
          className="input-vidrio"
          value={valor('unassigned') === 'true' ? 'none' : valor('membershipId')}
          onChange={(e) =>
            cambiar({
              membershipId: e.target.value === 'none' ? '' : e.target.value,
              unassigned: e.target.value === 'none' ? 'true' : '',
            } as Partial<Record<K, string>>)
          }
          aria-label="Licencia"
        >
          <option value="">Cualquier licencia</option>
          <option value="none">Sin ninguna licencia</option>
          {agruparLicencias(resumen.data?.memberships ?? []).map((g) => (
            <optgroup key={g.titulo} label={g.titulo}>
              {g.items.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.itemsCount})
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      )}
      {tiene('english') && (
        <select
          className="input-vidrio"
          value={valor('english')}
          onChange={(e) => poner('english', e.target.value)}
          aria-label="Idioma"
        >
          <option value="">Español e inglés</option>
          <option value="false">Español</option>
          <option value="true">Inglés</option>
        </select>
      )}
    </div>
  );
}
